import { routeLayerToCategory } from '../../../map-editor-shared/lib/layerUtils';
import { useObjectTypeStore } from '../../../map-editor-shared/store/objectTypeStore';
import { dia } from '@joint/core';
import type { TemplateRelationType } from '../../../map-editor-entities/schema/templateSchema';
import { isRelationshipProperty } from './export/relationshipExtractor';

export type RelationshipResetScope = 'all' | 'selected';

const category = (value: string) => {
  const routed = routeLayerToCategory(value);
  return routed === 'unknown' ? value : routed;
};
const pairKey = (a: string, b: string) => [category(a), category(b)].sort().join(':');
const labels: Record<string, string> = {
  cctv: 'CCTV',
  warningCctv: '단방향 CCTV',
  emergencyBell: '비상벨',
  parkingLocation: '주차면',
  zone: '구역',
  light: '조명',
  guideBoard: '안내현황판',
};

/** Relationship pairs are direction-independent, so reverse references are cleared together. */
export function relationshipResetOptions(relations: Record<string, TemplateRelationType>) {
  const pairs = [
    ['cctv', 'parkingLocation'],
    ['cctv', 'emergencyBell'],
    ['cctv', 'light'],
    ['cctv', 'cctv'],
    ['zone', 'cctv'],
    ['zone', 'warningCctv'],
    ['guideBoard', 'zone'],
    ['guideBoard', 'parkingLocation'],
    ...Object.values(relations).map((r) => [r.sourceType, r.targetType]),
  ];
  return Array.from(
    new Map(
      pairs.map(([a, b]) => [
        pairKey(a, b),
        {
          key: pairKey(a, b),
          label: `${labels[category(a)] || a}–${labels[category(b)] || b}`,
        },
      ]),
    ).values(),
  );
}

function elementCategory(element: dia.Cell): string {
  const data = element.get('data') || {};
  if (data.properties?.kind === 'zone') return 'zone';
  const type = data.typeId || element.get('objectTypeId');
  const stored = useObjectTypeStore.getState().types.find((t) => t.id === type);
  const candidates = [data.layer, data.type, stored?.name, type].filter(Boolean) as string[];
  return (
    candidates.map(routeLayerToCategory).find((value) => value !== 'unknown') || candidates[0] || ''
  );
}

/** Clear outgoing and incoming references without changing object identity or geometry. */
export function resetRelationships(
  graph: dia.Graph,
  scope: RelationshipResetScope,
  selectedIds: string[],
  relationTypes: Record<string, TemplateRelationType>,
  relationPairs?: string[],
): number {
  const selected = new Set(selectedIds.filter((id) => graph.getCell(id)?.isElement()));
  if (scope === 'selected' && selected.size === 0) return 0;
  const customKeys = new Set(Object.values(relationTypes).map((config) => config.propertyKey));
  const pairs = relationPairs ? new Set(relationPairs) : null;
  const matches = (element: dia.Cell, key: string, id: unknown): boolean => {
    if (!pairs) return true;
    const source = elementCategory(element);
    const target = graph.getCell(String(id));
    if (target?.isElement()) return pairs.has(pairKey(source, elementCategory(target)));
    // A missing target can still be classified by its source-specific template property.
    return Object.values(relationTypes).some(
      (config) =>
        category(config.sourceType) === source &&
        config.propertyKey === key &&
        pairs.has(pairKey(source, config.targetType)),
    );
  };
  let changed = 0;
  graph.startBatch('reset-relationships');
  try {
    for (const element of graph.getElements()) {
      const data = element.get('data') || {};
      const properties = data.properties || {};
      const next = { ...properties };
      const clearOutgoing = scope === 'all' || selected.has(String(element.id));
      let dirty = false;
      for (const [key, value] of Object.entries(properties)) {
        if (!isRelationshipProperty(key) && !customKeys.has(key)) continue;
        if (Array.isArray(value)) {
          const filtered = value.filter(
            (id) => !((clearOutgoing || selected.has(String(id))) && matches(element, key, id)),
          );
          if (filtered.length !== value.length) {
            next[key] = filtered;
            dirty = true;
          }
        } else if ((typeof value === 'string' && value !== '') || typeof value === 'number') {
          if ((clearOutgoing || selected.has(String(value))) && matches(element, key, value)) {
            next[key] = '';
            dirty = true;
          }
        }
      }
      if (dirty) {
        element.set('data', { ...data, properties: next });
        changed++;
      }
    }
    // These links only visualize auto-link results; they are not independent map objects.
    for (const link of graph.getLinks()) {
      if (!link.get('isAutoLinkVisualization')) continue;
      if (pairs) {
        const source = graph.getCell(String(link.source().id));
        const target = graph.getCell(String(link.target().id));
        if (
          !source ||
          !target ||
          !pairs.has(pairKey(elementCategory(source), elementCategory(target)))
        )
          continue;
      }
      if (
        scope === 'all' ||
        selected.has(String(link.source().id)) ||
        selected.has(String(link.target().id))
      ) {
        link.remove();
      }
    }
  } finally {
    graph.stopBatch('reset-relationships');
  }
  return changed;
}
